import { Link, useSearchParams } from '@remix-run/react';
import { ArrowDown, Filter } from '@solar-icons/react';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import Breadcrumbs from '~/components/breadcrumbs/Breadcrumbs';
import CmsTransformer from '~/components/cms/CmsTransformer';
import ActiveFilter from '~/components/filter/ActiveFilter';
import FilterOffcanvas from '~/components/filter/FilterOffcanvas';
import FiltersForm from '~/components/filter/FiltersForm';
import CustomPagination from '~/components/pagination/CustomPagination';
import ProductCard from '~/components/ProductCard';
import EmblaSlider from '~/components/Slider/EmblaSlider';
import ProductToolbar from '~/components/taxons/ProductToolbar';
import TaxonMiniature from '~/components/taxons/TaxonMiniature';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { useSlugTranslations } from '~/context/SlugTranslationsContext';
import { useViewItemList, type ListContext } from '~/gtm';
import { useProductFilters } from '~/hooks/useProductFilters';
import { useRoutePath } from '~/hooks/useRoutePath';
import Layout from '~/layouts/Default';
import { CategoryLoaderData } from '~/pages/CategoryPage';
import { FilterDefinition } from '~/types/Filter';
import { Product } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';
import { useFormatPrice } from '~/utils/price';

interface CategoryPageProps {
  serverData: CategoryLoaderData;
}

const PER_PAGE = 24;

const CategoryPage: React.FC<CategoryPageProps> = ({ serverData }) => {
  const { t } = useTranslation();
  const { setSlugTranslations } = useSlugTranslations();
  const productsRef = useRef<HTMLDivElement>(null);
  const loadMorePageRef = useRef<number | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchString = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    return params.toString();
  }, [searchParams]);

  const pageFromUrl = Number(searchParams.get('page')) || 1;

  // Track the last fetched params to avoid refetching on hydration
  const lastFetchedParams = useRef({ page: pageFromUrl, search: searchString });

  // Initialize state from server data
  const [products, setProducts] = useState<Product[]>(serverData.products);
  const [filters, setFilters] = useState<FilterDefinition[]>(serverData.filters);
  const [productLoading, setProductLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Track current page for pagination display (synced from URL, but also updated by load more)
  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  const [hasMore, setHasMore] = useState(serverData.hasMore);
  const [numberOfPages, setNumberOfPages] = useState(serverData.numberOfPages);
  const [totalProductsCount, setTotalProductsCount] = useState(serverData.totalProductsCount);
  const [filteredProductsCount, setFilteredProductsCount] = useState(serverData.filteredProductsCount);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionTruncated, setDescriptionTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const { addMessage } = useFlashMessages();
  const API = serverData.API_URL;
  const routePath = useRoutePath();
  const formatPrice = useFormatPrice();

  const taxon = serverData.taxon;
  const taxonCode = taxon?.code;

  // GTM: List context for product tracking
  const listContext: ListContext = useMemo(
    () => ({
      item_list_id: `category_${taxonCode}`,
      item_list_name: taxon?.name || 'Category',
    }),
    [taxonCode, taxon?.name]
  );

  // GTM: Get default currency from first product
  const defaultCurrency = products[0]?.defaultVariantData?.currency || 'CZK';

  // GTM: Track view_item_list with IntersectionObserver
  const { observeProduct } = useViewItemList({
    listContext,
    currency: defaultCurrency,
    products,
  });

  // Set slug translations for language switcher
  useEffect(() => {
    if (taxon?.slugTranslations) {
      setSlugTranslations(taxon.slugTranslations);
    }
    return () => setSlugTranslations(null);
  }, [taxon?.slugTranslations, setSlugTranslations]);

  // Sync state when navigating to a different category (serverData changes)
  useEffect(() => {
    setProducts(serverData.products);
    setFilters(serverData.filters);
    setHasMore(serverData.hasMore);
    setNumberOfPages(serverData.numberOfPages);
    setTotalProductsCount(serverData.totalProductsCount);
    setFilteredProductsCount(serverData.filteredProductsCount);
    lastFetchedParams.current = { page: 1, search: '' };
    setDescriptionExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonCode]);

  // Sync currentPage from URL (for direct URL access or browser navigation)
  // Skip if this was triggered by load more
  useEffect(() => {
    if (loadMorePageRef.current === pageFromUrl) {
      return;
    }
    setCurrentPage(pageFromUrl);
  }, [pageFromUrl]);

  // Check if description is truncated
  const checkTruncation = useCallback(() => {
    const el = descriptionRef.current;
    if (el && !descriptionExpanded) {
      const isTruncated = el.scrollHeight > el.clientHeight;
      setDescriptionTruncated(isTruncated);
    }
  }, [descriptionExpanded]);

  // Run truncation check after DOM update
  useEffect(() => {
    checkTruncation();
  }, [taxon?.description, checkTruncation]);

  // Re-check truncation on window resize
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkTruncation, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [checkTruncation]);

  const fetchProducts = useCallback(
    async (page: number, code: string, queryParams: string, signal?: AbortSignal): Promise<Product[]> => {
      const url = `${API}/api/v2/shop/products/search?limit=${PER_PAGE}&page=${page}&facets[taxon][]=${code}&imageFilter=sylius_shop_product_thumbnail${
        queryParams ? `&${queryParams}` : ''
      }`;
      const response = await apiFetch(url, { signal });
      const data = await response.json();
      const pagesCount = data.pagination?.total_pages || 1;
      const more = data.pagination?.has_next_page || false;
      const filteredCount = data.pagination?.total_items || 0;
      setNumberOfPages(pagesCount);
      setHasMore(more);
      setFilteredProductsCount(filteredCount);
      return data.products || [];
    },
    [API]
  );

  const fetchFilters = useCallback(
    async (code: string, queryParams: string, signal?: AbortSignal): Promise<FilterDefinition[]> => {
      const res = await apiFetch(`${API}/api/v2/shop/taxon-filters/${code}${queryParams ? `?${queryParams}` : ''}`, {
        signal,
      });
      const json = await res.json();
      setTotalProductsCount(json.taxonProducts || 0);
      return Object.values(json.filters ?? {});
    },
    [API]
  );

  // Refetch products/filters when URL params change (skip if params haven't changed - handles hydration)
  useEffect(() => {
    // Skip if params haven't actually changed (handles hydration and unstable deps)
    const paramsChanged =
      lastFetchedParams.current.page !== pageFromUrl || lastFetchedParams.current.search !== searchString;

    if (!paramsChanged) {
      return undefined;
    }

    if (!taxonCode) return undefined;
    if (loadMorePageRef.current === pageFromUrl) {
      loadMorePageRef.current = null;
      return undefined;
    }

    lastFetchedParams.current = { page: pageFromUrl, search: searchString };

    const abortController = new AbortController();

    const loadProducts = async () => {
      setProductLoading(true);

      try {
        const [productsData, filtersData] = await Promise.all([
          fetchProducts(pageFromUrl, taxonCode, searchString, abortController.signal),
          fetchFilters(taxonCode, searchString, abortController.signal),
        ]);

        setFilters(filtersData);
        setProducts(productsData);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        addMessage('error', t('pages.category.error_load_products'));
        console.error('[products load error]', err);
      } finally {
        if (!abortController.signal.aborted) {
          setProductLoading(false);
        }
      }
    };

    loadProducts().catch(console.error);

    return () => abortController.abort();
  }, [taxonCode, pageFromUrl, searchString, fetchProducts, fetchFilters, addMessage, t]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !taxonCode) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const newProducts = await fetchProducts(nextPage, taxonCode, searchString);
      setProducts((prev) => [...prev, ...newProducts]);

      loadMorePageRef.current = nextPage;
      setCurrentPage(nextPage);
      // Update URL without triggering Remix navigation (avoids progress bar)
      const params = new URLSearchParams(searchParams);
      params.set('page', nextPage.toString());
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
    } catch (err) {
      addMessage('error', t('pages.category.error_load_more'));
      console.error('[load more error]', err);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, taxonCode, hasMore, loadingMore, searchString, searchParams, fetchProducts, addMessage, t]);

  const handlePageChange = (page: number) => {
    if (page === currentPage) return;

    // Reset load more ref so the fetch effect knows this is a pagination click
    loadMorePageRef.current = null;
    setCurrentPage(page);

    const params = new URLSearchParams(searchParams);

    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }

    setSearchParams(params, { preventScrollReset: true });
    if (productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleToggleFilterOffcanvas = () => {
    setShowFilterOffcanvas(!showFilterOffcanvas);
  };

  const breadcrumbs = [
    { label: t('common.home'), url: routePath('homepage') },
    ...serverData.breadcrumbs.map((p) => ({
      label: p.name,
      url: routePath('category', p.slug),
    })),
  ];

  const {
    activeFilters,
    applyFilters: rawApplyFilters,
    removeFilter: rawRemoveFilter,
    filterLabelMap,
    optionLabelMap,
    currencyMap,
    clearFilters: rawClearFilters,
  } = useProductFilters(filters);

  const scrollToProducts = () => productsRef.current?.scrollIntoView({ behavior: 'smooth' });

  const applyFilters = useCallback(
    (filtersToApply: Parameters<typeof rawApplyFilters>[0]) => {
      rawApplyFilters(filtersToApply);
    },
    [rawApplyFilters]
  );

  const removeFilter = useCallback(
    (key: string, value?: string) => {
      rawRemoveFilter(key, value);
      scrollToProducts();
    },
    [rawRemoveFilter]
  );

  const clearFilters = useCallback(() => {
    rawClearFilters();
    scrollToProducts();
  }, [rawClearFilters]);

  return (
    <Layout>
      <Container>
        <Breadcrumbs paths={breadcrumbs} />
        <div className="category-header">
          <h1 className="category-title">{taxon?.name}</h1>
          {taxon?.description && taxon.description.length > 0 && (
            <div
              key={taxonCode}
              className={`category-description-wrapper${descriptionExpanded ? ' expanded' : ''}${descriptionTruncated && !descriptionExpanded ? ' truncated' : ''}`}
            >
              <div ref={descriptionRef} className="category-description">
                {/* No `my-4` wrapper: the description's own spacing is set on
                    `.category-description-wrapper`, and the default would add 24px above
                    and below every block, pushing the products off the first screen. */}
                {taxon.description.map((block, index) => (
                  <CmsTransformer item={block} key={index} className="" />
                ))}
              </div>
              {descriptionTruncated && !descriptionExpanded && (
                <Button variant="link" className="mx-auto p-0" onClick={() => setDescriptionExpanded(true)}>
                  {t('read_more')}
                  <ArrowDown weight="Outline" size={20} />
                </Button>
              )}
            </div>
          )}
          {taxon && Array.isArray(taxon.children) && taxon.children.length > 0 && (
            <div className="category-children">
              <EmblaSlider
                slideClass="category-children__slide"
                className="embla--branded"
                spacing={false}
                options={{ align: 'start', containScroll: 'trimSnaps' }}
              >
                {taxon.children.map((child) => (
                  <TaxonMiniature
                    taxon={{
                      slug: child.slug,
                      title: child.name,
                      image: child.images?.[0]?.path || '',
                    }}
                    key={child.code}
                  />
                ))}
              </EmblaSlider>
            </div>
          )}
        </div>

        <Row className="category-content">
          <Col xs={12} lg={3}>
            <FiltersForm
              filters={filters}
              onChange={applyFilters}
              initialValues={activeFilters}
              totalProductsCount={totalProductsCount}
              filteredProductsCount={filteredProductsCount}
              loading={productLoading}
            />
            {taxon?.banner?.image && (
              <Link to={taxon.banner.link || '#'} className="category-banner d-none d-lg-block">
                <img
                  src={taxon.banner.image}
                  alt={taxon.name}
                  className="category-banner__image img-fluid"
                  loading="lazy"
                  fetchpriority="low"
                  decoding="async"
                />
              </Link>
            )}
          </Col>

          <Col xs={12} lg={9} ref={productsRef}>
            <div className="category-products-top">
              <div className="active-filters">
                {Object.entries(activeFilters).map(([key, value]) => {
                  const filterLabel = filterLabelMap[key] ?? key;
                  if (Array.isArray(value) && typeof value[0] === 'number') {
                    const currency = currencyMap[key];
                    const displayValue = currency
                      ? `${formatPrice(value[0], currency)} - ${formatPrice(value[1], currency)}`
                      : `${value[0]} - ${value[1]}`;
                    return (
                      <ActiveFilter
                        label={filterLabel}
                        value={displayValue}
                        onClick={() => removeFilter(key)}
                        key={key}
                      />
                    );
                  }

                  return (value as string[]).map((v) => {
                    const optionLabel = optionLabelMap[key]?.[v] ?? v;

                    return (
                      <ActiveFilter
                        label={filterLabel}
                        value={optionLabel}
                        onClick={() => removeFilter(key, v)}
                        key={`${key}-${v}`}
                      />
                    );
                  });
                })}
                {activeFilters && Object.keys(activeFilters).length > 0 && (
                  <Button variant="link" onClick={clearFilters}>
                    {t('components.filter.clear_all')}
                  </Button>
                )}
              </div>
              <div className="category-filter__mobile">
                <Button
                  variant="outline-secondary"
                  onClick={handleToggleFilterOffcanvas}
                  className="category-filter__button fw-medium"
                >
                  <Filter weight="Outline" size={24} color="#F96D3B" />
                  <span className="fw-medium text-uppercase">{t('components.filter.title')}</span>
                </Button>
                {activeFilters && Object.keys(activeFilters).length > 0 && (
                  <Button variant="link" onClick={clearFilters} className="category-filter__clear-mobile">
                    {t('components.filter.clear_all')}
                  </Button>
                )}
              </div>
              <FilterOffcanvas
                show={showFilterOffcanvas}
                handleClick={handleToggleFilterOffcanvas}
                filters={filters}
                onChange={applyFilters}
                onClearFilters={clearFilters}
                initialValues={activeFilters}
                filteredProductsCount={filteredProductsCount}
                loading={productLoading}
              />
              <div className="category-sort__mobile">
                <ProductToolbar />
                <p className="category-sort__items-shown">
                  <span>
                    <Trans
                      i18nKey="components.filter.items_shown"
                      values={{ count: filteredProductsCount, total: totalProductsCount }}
                      components={[<strong />]}
                    />
                  </span>
                </p>
              </div>
            </div>
            <Row>
              {productLoading
                ? Array.from({ length: PER_PAGE }).map((_, i) => (
                    <Col key={i} xs={6} md={3} className="category-product">
                      <Skeleton key={i} height={300} />
                    </Col>
                  ))
                : products.map((product, index) => (
                    <Col key={product.id} xs={6} md={3} className="category-product">
                      <ProductCard
                        product={product}
                        lazyloaded={index >= 2}
                        listContext={listContext}
                        index={index}
                        onProductRef={observeProduct}
                      />
                    </Col>
                  ))}
            </Row>
            {products.length > 0 && !productLoading && (
              <div className="category-footer">
                {!loadingMore && hasMore && (
                  <Button variant="outline-primary" onClick={loadMore}>
                    <span>
                      {t('common.load_more')} ({Math.min(PER_PAGE, filteredProductsCount - products.length)})
                    </span>{' '}
                    <ArrowDown weight="Linear" size={18} />
                  </Button>
                )}
              </div>
            )}
            {loadingMore && (
              <div className="text-center mt-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            )}
            {products.length > 0 && !productLoading && (
              <CustomPagination
                numberOfPages={numberOfPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
            {products.length === 0 && !productLoading && (
              <div className="text-center h3 mt-5">
                <strong>{t('common.no_products')}</strong>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default CategoryPage;
